import { createCounter } from "../utils/general";
// Phrases that help us deftermine key metrics to style differently
const keyMetricsPhrases = [
    "total",
    "net income",
    "net loss",
    "earnings per share",
];
export async function writeReportToExcel(worksheetName, cells) {
    return await Excel.run(async (context) => {
        // Set calculation mode to manual and save existing setting
        const originalCalcMode = await setManualCalculation(context);
        // Create a new sheet
        const sheet = context.workbook.worksheets.add(worksheetName);
        // Get values and formulas to insert, as well as all values
        const valuesAndFormulas = buildValuesAndFormulas(cells);
        // Get row and column count
        const rowCount = valuesAndFormulas.valuesToSet.length;
        const colCount = valuesAndFormulas.valuesToSet[0]?.length || 0;
        // Apply both sets to Excel
        const range = sheet.getRangeByIndexes(0, 0, rowCount, colCount);
        range.values = valuesAndFormulas.valuesToSet;
        range.formulas = valuesAndFormulas.formulasToSet;
        // Freeze column A and row 1
        sheet.freezePanes.freezeRows(1);
        sheet.freezePanes.freezeColumns(1);
        // Get formatting groups for batch formatting operations
        const groups = computeFormatGroups(valuesAndFormulas.allValues, keyMetricsPhrases);
        /**
         * Here we apply batch formatting. In older versions of Excel, formatting all at once, with a single
         * context.sync() proved too resource-intensive, often crashing the application.
         */
        await applyFormattingBatched(context, sheet, colCount, groups);
        // After setting all values and applying formatting, set column widths
        range.getEntireColumn().format.autofitColumns();
        // Format special cells
        formatSpecialCells(sheet);
        // Reset calculation mode to original
        restoreCalculationMode(context, originalCalcMode);
        await context.sync();
        // Bring the new sheet to the foreground
        sheet.activate();
        // Select the first cell, scroll to top left
        sheet.getRange("B2").select();
        // Final sync
        await context.sync();
    });
}
async function setManualCalculation(context) {
    context.application.load("calculationMode");
    await context.sync();
    const original = context.application.calculationMode;
    context.application.calculationMode = Excel.CalculationMode.manual;
    await context.sync();
    return original;
}
function restoreCalculationMode(context, original) {
    context.workbook.application.calculate("Full");
    context.application.calculationMode = original;
}
function buildValuesAndFormulas(cells) {
    const rowCount = cells[0]?.length || 0;
    const colCount = cells.length;
    // Initialize arrays with correct shape
    const valuesAndFormulas = {
        allValues: Array.from({ length: rowCount }, () => Array(colCount).fill(null)),
        valuesToSet: Array.from({ length: rowCount }, () => Array(colCount).fill(null)),
        formulasToSet: Array.from({ length: rowCount }, () => Array(colCount).fill(null)),
    };
    cells[0].forEach((_, rowIndex) => {
        cells.forEach((column, colIndex) => {
            const cell = column[rowIndex];
            const parsedValue = parseValueForExcel(cell.text);
            if (cell.formula) {
                valuesAndFormulas.formulasToSet[rowIndex][colIndex] = cell.formula;
            }
            else {
                valuesAndFormulas.valuesToSet[rowIndex][colIndex] = parsedValue;
            }
            /**
             * All values are passed back from the API alongside formulas. When applying values
             * to the sheet, we need the formulas if they exist, and the values if they do not.
             * However, in order to correctly format the text, such as setting the correct decimal
             * places for a numeric value, we need the original value passed back from the API. This
             * value is already formatted correctly. If we rely on the formula to be evaluated and then
             * read the value to determine how many decimal places to preserve, we end up with many
             * decimal places and no way to reverse engineer that.
             */
            valuesAndFormulas.allValues[rowIndex][colIndex] = parsedValue;
        });
    });
    return valuesAndFormulas;
}
function computeFormatGroups(values, keyMetrics) {
    const rowCount = values.length;
    const groups = {
        headerRows: [],
        sectionHeaderRows: [],
        keyMetricRows: [],
        normalMetricRows: [],
        numericFormatByDecimals: {},
    };
    // Compute headers and section headers
    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
        const row = values[rowIndex];
        const isHeader = row.slice(1).every((cell) => cell === null);
        if (isHeader) {
            const isSectionHeader = rowIndex >= 1 &&
                (values[rowIndex - 1].every((value) => value === null) ||
                    rowIndex === 1) &&
                rowIndex + 1 < values.length &&
                values[rowIndex + 1].slice(1).every((value) => value === null);
            if (isSectionHeader) {
                groups.sectionHeaderRows.push(rowIndex);
            }
            else {
                groups.headerRows.push(rowIndex);
            }
            continue;
        }
        // Compute metrics and key metrics
        const cellText = row[0] ?? "";
        if (typeof cellText === "string" &&
            keyMetrics.some((word) => cellText.toLowerCase().includes(word))) {
            groups.keyMetricRows.push(rowIndex);
        }
        else {
            groups.normalMetricRows.push(rowIndex);
        }
        // Numeric formatting
        let maxDecimals = 0;
        let hasNumeric = false;
        for (const v of row) {
            if (typeof v === "number") {
                hasNumeric = true;
                const decimals = (String(v).split(".")[1] || "").length;
                if (decimals > maxDecimals) {
                    maxDecimals = decimals;
                }
            }
        }
        // If numeric, it will require formatting
        if (hasNumeric) {
            groups.numericFormatByDecimals[maxDecimals] ??= [];
            groups.numericFormatByDecimals[maxDecimals].push(rowIndex);
        }
    }
    return groups;
}
function rowToRangeReference(rowIndex, columnCount) {
    /**
     * Row index 0 is equivalent to row number 1 (human readable in Excel)
     * A column count of 10 means the last column is column index 9
     */
    const rowNumber = rowIndex + 1;
    const columnName = columnIndexToColumnName(columnCount - 1);
    return `A${rowNumber}:${columnName}${rowNumber}`;
}
function blockToRangeReference(firstRowIndex, lastRowIndex, columnCount) {
    /**
     * Row index 0 is equivalent to row number 1 (human readable in Excel)
     * A column count of 10 means the last column is column index 9
     */
    const firstRowNumber = firstRowIndex + 1;
    const lastRowNumber = lastRowIndex + 1;
    const lastColumnName = columnIndexToColumnName(columnCount - 1);
    return `A${firstRowNumber}:${lastColumnName}${lastRowNumber}`;
}
function columnIndexToColumnName(n) {
    let columnName = "";
    while (n >= 0) {
        columnName = String.fromCharCode((n % 26) + 65) + columnName;
        n = Math.floor(n / 26) - 1;
    }
    return columnName;
}
async function applyFormattingBatched(context, sheet, columnCount, groups) {
    /**
     * Here we strive to strike a balance between syncing too often and not enough
     * Syncing not enough resulted in some environments crashing when too many
     * operations were queued for a final sync
     */
    if (groups.headerRows.length) {
        const firstColRefs = groups.headerRows.map((r) => `A${r + 1}`).join(",");
        const ranges = sheet.getRanges(firstColRefs);
        ranges.format.indentLevel = 2;
        ranges.format.font.bold = true;
        ranges.format.verticalAlignment = "Bottom";
    }
    // Sync only headers
    await context.sync();
    if (groups.sectionHeaderRows.length) {
        const firstColRefs = groups.sectionHeaderRows
            .map((r) => `A${r + 1}:A${r + 2}`)
            .join(",");
        const ranges = sheet.getRanges(firstColRefs);
        ranges.format.indentLevel = 1;
        ranges.format.font.bold = true;
        ranges.format.verticalAlignment = "Bottom";
    }
    // Sync only section headers
    await context.sync();
    if (groups.keyMetricRows.length) {
        // All values in key metrics rows are bold
        const keyMetricRangeReferences = groups.keyMetricRows
            .map((rowIndex) => {
            return rowToRangeReference(rowIndex, columnCount);
        })
            .join(",");
        const keyMetricRanges = sheet.getRanges(keyMetricRangeReferences);
        keyMetricRanges.format.font.bold = true;
        // Key metrics titles (column 1) are indented 3
        const keyMetricTitleRangeReferences = groups.keyMetricRows
            .map((rowIndex) => {
            return rowToRangeReference(rowIndex, 1);
        })
            .join(",");
        const keyMetricTitleRanges = sheet.getRanges(keyMetricTitleRangeReferences);
        keyMetricTitleRanges.format.indentLevel = 3;
    }
    // Sync only key metrics
    await context.sync();
    if (groups.normalMetricRows.length) {
        const rangeReferences = groups.normalMetricRows
            .map((rowIndex) => {
            return rowToRangeReference(rowIndex, 1);
        })
            .join(",");
        sheet.getRanges(rangeReferences).format.indentLevel = 3;
    }
    // Sync only normal metrics
    await context.sync();
    // Batch and sync each set of decimal places
    for (const [decStr, rows] of Object.entries(groups.numericFormatByDecimals)) {
        const dec = Number(decStr);
        let fmt = "#,##0";
        if (dec > 0)
            fmt += "." + "0".repeat(dec);
        const blocks = collapseToContiguousBlocks(rows);
        for (const [start, end] of blocks) {
            const rangeReference = blockToRangeReference(start, end, columnCount);
            const range = sheet.getRange(rangeReference);
            const rowCount = end - start + 1;
            const formatMatrix = Array.from({ length: rowCount }, () => Array(columnCount).fill(fmt));
            range.numberFormatLocal = formatMatrix;
            await context.sync();
        }
    }
}
function collapseToContiguousBlocks(rows) {
    /**
     * Here we aim to collapse adjacent rows in order to perform fewer
     * range operations
     */
    if (!rows.length)
        return [];
    const sorted = [...new Set(rows)].sort((a, b) => a - b);
    const blocks = [];
    let start = sorted[0];
    let prev = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === prev + 1) {
            prev = sorted[i];
            continue;
        }
        blocks.push([start, prev]);
        start = sorted[i];
        prev = sorted[i];
    }
    blocks.push([start, prev]);
    return blocks;
}
function formatSpecialCells(sheet) {
    // Special row 1 formatting
    const headerRow = sheet.getRange("1:1");
    headerRow.format.rowHeight = 40;
    headerRow.format.font.bold = true;
    headerRow.format.horizontalAlignment = "Right";
    headerRow.format.verticalAlignment = "Bottom";
    // Special cell A1 formatting
    const cellA1 = sheet.getRange("A1");
    cellA1.format.horizontalAlignment = "Left";
    cellA1.format.verticalAlignment = "Bottom";
    cellA1.format.indentLevel = 1;
    cellA1.format.font.size = 14;
    // Auto-width, applied earlier, is inappropriate for Column A, as it can become very wide
    // We explicitly set it here
    const colA = sheet.getRange("A:A");
    colA.format.columnWidth = 250;
}
function parseValueForExcel(raw) {
    if (raw === null || raw === "")
        return null;
    const num = Number(raw);
    return Number.isNaN(num) ? raw : num;
}
/**
 * Find the column based on year and quarter
 */
async function findColumnForYearAndQuarter(context, year, quarter, numberOfRowsToTry = 12) {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    const usedRange = sheet.getUsedRange();
    usedRange.load(["columnCount", "columnIndex"]);
    // Sync after load
    await context.sync();
    /**
     * We want to avoid analyzing the entire sheet, as the details we need are likely
     * towards the top. Default to top 12 rows unless told otherwise
     */
    const range = sheet.getRangeByIndexes(0, 0, numberOfRowsToTry, usedRange.columnCount + usedRange.columnIndex);
    range.load(["values", "rowIndex", "columnIndex", "rowCount", "columnCount"]);
    // Get merged areas to un-merge
    const mergedAreas = range.getMergedAreasOrNullObject();
    mergedAreas.load(["isNullObject", "areas"]);
    // Sync after loads
    await context.sync();
    // Cell values
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const values = range.values;
    if (!mergedAreas.isNullObject && mergedAreas.areas.items.length > 0) {
        mergedAreas.areas.items.forEach((range) => {
            range.load([
                "rowIndex",
                "columnIndex",
                "rowCount",
                "columnCount",
                "values",
            ]);
        });
        // Sync after load
        await context.sync();
        /**
         * When we have a set of cells like this:
         *
         * |               2025                |
         * |   Q1   |   Q2   |   Q3   |   Q4   |
         *
         * Where 2025 is merged across four columns
         * We need to un-merge the cells to create this:
         *
         * |  2025  |  2025  |  2025  |  2025  |
         * |   Q1   |   Q2   |   Q3   |   Q4   |
         *
         * So that when we try to determine the correct
         * column for a year and quarter, and analyze
         * adjacent row values, it succeeds.
         */
        mergedAreas.areas.items.forEach((area) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const val = area.values[0][0];
            const rowOffset = area.rowIndex - range.rowIndex;
            const colOffset = area.columnIndex - range.columnIndex;
            for (let r = 0; r < area.rowCount; r++) {
                for (let c = 0; c < area.columnCount; c++) {
                    values[rowOffset + r][colOffset + c] = val;
                }
            }
        });
    }
    // Counts for loop below
    const columnCount = range.columnCount;
    const rowCount = range.rowCount;
    // To keep track of matching columns
    const matches = [];
    // Regular expressions for discerning quarter or year + quarter cells
    const yearStr = String(year);
    const yearQuarterPatterns = [
        new RegExp(`[Qq]${quarter}[-' ]*${yearStr.slice(2)}[EA]?`, "i"), // q424, Q4-24, Q4'24E, Q4 '24A
        new RegExp(`${quarter}[Qq][-' ]*${yearStr.slice(2)}[EA]?`, "i"), // 4q24, 4Q-24, 4Q'24E, 4Q '24A
        new RegExp(`[Qq]${quarter}-${yearStr}`, "i"), // Q4-2024, q4-2024
        new RegExp(`${yearStr}-[Qq]${quarter}`), // 2024-Q4, 2024-q4
    ];
    const quarterPatterns = [
        new RegExp(`[Qq]${quarter}[EA]?`, "i"), // Q2, Q2E, Q2A, q2, q2E, q2A
        new RegExp(`${quarter}[Qq][EA]?`, "i"), // 2Q, 2QE, 2QA, 2q, 2qE, 2qA
    ];
    // Loop through columns, looking for matches
    for (let c = 0; c < columnCount; c++) {
        let found = false;
        // Scan each row in the column
        for (let r = 0; r < rowCount; r++) {
            const cell = String(values[r][c]);
            // 1. Single-cell match
            if (yearQuarterPatterns.some((p) => p.test(cell))) {
                found = true;
                break;
            }
            // 2. Multi-row match: year above, quarter below
            if (r < rowCount - 1) {
                const cellBeneath = String(values[r + 1][c]);
                if (cell.includes(yearStr) &&
                    quarterPatterns.some((p) => p.test(cellBeneath))) {
                    found = true;
                    break;
                }
            }
        }
        if (found)
            matches.push(c);
    }
    // If we matched more than one cell, abort, as we can't be certain which column is correct
    if (matches.length !== 1)
        return null;
    // Convert back to column letter from index
    return columnIndexToColumnName(matches[0]);
}
/**
 * Find the column based on year and quarter
 */
export async function findColumnForLineItems(context, threshold = 0.4) {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    const usedRange = sheet.getUsedRange();
    usedRange.load(["values", "columnIndex", "rowCount", "columnCount"]);
    // Sync after load
    await context.sync();
    const { values, rowCount, columnCount, columnIndex } = usedRange;
    // Set initial to invalid column index
    let targetColumnIndex = -1;
    // Labeled loops for breaking
    loop1: for (let col = 0; col < columnCount; col++) {
        let textCount = 0;
        for (let row = 0; row < rowCount; row++) {
            const cellValue = values[row][col];
            // Count it if there is actual textual content in the cell
            if (typeof cellValue === "string" &&
                cellValue !== null &&
                cellValue !== "") {
                textCount++;
            }
            // Once we hit our threshold, exit to avoid unnecessary computation
            if (textCount / rowCount >= threshold) {
                targetColumnIndex = col;
                break loop1;
            }
        }
    }
    if (targetColumnIndex >= 0) {
        // Convert back to column letter from index
        return columnIndexToColumnName(targetColumnIndex + columnIndex);
    }
    return null;
}
export async function setColumnBasedOnYearAndQuarter(context, setValue, fieldName, year, quarter, shouldValidate) {
    let columnName = await findColumnForYearAndQuarter(context, year, quarter);
    // setValue does not accept null values
    if (columnName === null)
        columnName = undefined;
    // Validation should happen at all times except for initial form load
    const setValueOptions = shouldValidate
        ? { shouldValidate: true }
        : { shouldValidate: false, shouldDirty: false, shouldTouch: false };
    setValue(fieldName, columnName, setValueOptions);
}
export async function setLineItemColumn(context, setValue, fieldName, shouldValidate) {
    let columnName = await findColumnForLineItems(context);
    // setValue does not accept null values
    if (columnName === null)
        columnName = undefined;
    // Validation should happen at all times except for initial form load
    const setValueOptions = shouldValidate
        ? { shouldValidate: true }
        : { shouldValidate: false, shouldDirty: false, shouldTouch: false };
    setValue(fieldName, columnName, setValueOptions);
}
export async function setSheetsSelectOptions(context, setModelSheetSelectOptions) {
    // Load all sheets
    const sheets = context.workbook.worksheets;
    sheets.load("items/name,items/visibility");
    await context.sync();
    // Add as options
    const sheets_to_add = [];
    sheets.items.forEach((item) => {
        if (item.visibility === "Visible") {
            sheets_to_add.push(item.name);
        }
    });
    setModelSheetSelectOptions(sheets_to_add);
}
export async function setActiveSheet(context, setValue, fieldName, shouldValidate) {
    // Get active sheet to select
    const activeSheet = context.workbook.worksheets.getActiveWorksheet();
    activeSheet.load("name");
    // Await after load
    await context.sync();
    const activeSheetName = activeSheet.name ?? undefined;
    const setValueOptions = shouldValidate
        ? { shouldValidate: true }
        : { shouldValidate: false, shouldDirty: false, shouldTouch: false };
    setValue(fieldName, activeSheetName, setValueOptions);
}
export async function getContext(callback) {
    return Excel.run(async (context) => {
        return callback(context);
    });
}
export async function buildModelUpdaterPayload(context, worksheet, form_data) {
    const workbook = context.workbook;
    workbook.load("name");
    worksheet.load("name");
    await context.sync();
    const payload = {
        ticker: form_data.ticker,
        excel_file_name: workbook.name,
        source_urls: form_data.source_urls,
        llm_model: form_data.llm_model,
        hot_topics: form_data.hot_topics,
        return_summary: form_data.return_summary,
        overwrite_target: form_data.overwrite_target,
        line_items: {
            excel_sheet: worksheet.name,
            column: form_data.line_items_column,
            rows: [],
        },
        target_period: {
            excel_sheet: worksheet.name,
            year: Number(form_data.target_year),
            column: form_data.target_column,
            rows: [],
        },
        comparison_period: {
            excel_sheet: worksheet.name,
            year: Number(form_data.comparison_year),
            column: form_data.comparison_column,
            rows: [],
        },
    };
    const target_quarter_int = Number(form_data.target_quarter);
    const comparison_quarter_int = Number(form_data.comparison_quarter);
    if (target_quarter_int >= 1 && target_quarter_int <= 4)
        payload.target_period.quarter = target_quarter_int;
    if (target_quarter_int >= 5)
        payload.target_period.half = target_quarter_int - 4; // h1 = 5, h2 = 6
    if (comparison_quarter_int >= 1 && comparison_quarter_int <= 4)
        payload.comparison_period.quarter = comparison_quarter_int;
    if (comparison_quarter_int >= 5)
        payload.comparison_period.half = comparison_quarter_int - 4; // h1 = 5, h2 = 6
    // unmerge any merged columns before pulling them into the request
    const entire_sheet = worksheet.getRange();
    entire_sheet.unmerge();
    await context.sync();
    // if "overwrite target" mode is false, skip rows with values in the target period
    const skip_list = await getSkipList(context, worksheet, payload.line_items.column, payload.target_period.column, payload.overwrite_target);
    payload.line_items.rows = await getColumnValues(context, worksheet, payload.line_items.column, skip_list, form_data.first_row_number, form_data.last_row_number);
    payload.comparison_period.rows = await getColumnValues(context, worksheet, payload.comparison_period.column, skip_list, form_data.first_row_number, form_data.last_row_number);
    return payload;
}
async function getSkipList(context, worksheet, line_items_column, target_period_column, overwrite_target) {
    const target_col = worksheet
        .getRange(`${target_period_column}:${target_period_column}`)
        .getUsedRange();
    const line_items_col = worksheet
        .getRange(`${line_items_column}:${line_items_column}`)
        .getUsedRange();
    target_col.load({ select: "text,rowIndex" });
    line_items_col.load({ select: "text,rowIndex" });
    await context.sync();
    const skip_list = [];
    // skip anything with "guidance" in the line items
    line_items_col.text.forEach((row, index) => {
        if (row[0].toLowerCase().includes("guidance")) {
            skip_list.push(index + target_col.rowIndex + 1);
        }
    });
    if (overwrite_target) {
        return skip_list;
    }
    // if overwrite target is false, skip rows that already have target period values
    target_col.text.forEach((row, index) => {
        if (row[0] == "" || row[0] == "No Value") {
            // do not skip
        }
        else {
            skip_list.push(index + target_col.rowIndex + 1);
        }
    });
    return skip_list;
}
async function getColumnValues(context, worksheet, column, skip_list, first_row_number, last_row_number) {
    const rows = [];
    const range = worksheet.getRange(`${column}:${column}`).getUsedRange();
    range.load({ select: "text,formulas,rowIndex" });
    await context.sync();
    // Excel gives us an array of arrays for the text and formulas of the range
    // Outer array is rows, inner array is columns
    range.text.forEach((row, index) => {
        const text = row[0];
        const formulaString = range.formulas[index][0].toString(); // could be a number type
        const formulaExEquals = formulaString.substring(1);
        const rowNumber = index + range.rowIndex + 1;
        // for formulas that are just a number, strip the formula and send the number:
        if (skip_list.includes(rowNumber)) {
            // do nothing
        }
        else if (rowNumber < first_row_number) {
            // do nothing
        }
        else if (rowNumber > last_row_number) {
            // do nothing
        }
        else if (text &&
            formulaString[0] === "=" &&
            !isNaN(formulaString.substring(1))) {
            rows.push({ row: rowNumber, text: formulaExEquals });
            // if there's a formula, send the formula:
        }
        else if (text && formulaString[0] === "=") {
            rows.push({ row: rowNumber, text, formula: formulaString });
            // otherwise, send the string:
        }
        else if (text) {
            rows.push({ row: rowNumber, text });
        }
    });
    return rows;
}
export async function insertSummarySheet(context, data, timestamp, ticker, year, quarter, source_urls) {
    const sheet = context.workbook.worksheets.add(`Summary - ${timestamp}`);
    if (data.summary &&
        Array.isArray(data.summary) &&
        data.summary.length &&
        Array.isArray(data.summary[0])) {
        // We start on row 2
        const rowCounterColumbB = createCounter(2, 1);
        const margin_column = sheet.getRange(`A:A`);
        margin_column.format.columnWidth = 20;
        const title_cell = sheet.getRange(`B${rowCounterColumbB.next().value}`);
        title_cell.values = [["Update Summary"]];
        title_cell.format.font.size = 18;
        title_cell.format.font.bold = true;
        let quarterString;
        quarterString ??= `FY`;
        if (quarter === 5) {
            quarterString = "1H";
        }
        else if (quarter === 6) {
            quarterString = "2H";
        }
        else if (quarter === 0) {
            quarterString = "FY";
        }
        const subtitle_cell = sheet.getRange(`B${rowCounterColumbB.next().value}`);
        subtitle_cell.values = [[`${ticker} ${year}-${quarterString}`]];
        rowCounterColumbB.next(); // spacing
        for (const source_url of source_urls) {
            const url_cell = sheet.getRange(`B${rowCounterColumbB.next().value}`);
            url_cell.values = [[`=hyperlink("${source_url}")`]];
            url_cell.format.font.underline = `Single`;
            url_cell.format.font.color = `#0000FF`;
        }
        const result = rowCounterColumbB.next();
        if (!result.done && result.value !== undefined) {
            const content_row_start = result.value;
            for (const item of data.summary[0]) {
                if (item.row && item.text) {
                    const cell = sheet.getRange(`B${content_row_start + item.row}`);
                    cell.values = [[item.text]];
                }
            }
        }
    }
    else {
        sheet.getRange("B1").values = [["No summary data provided"]];
    }
    //sheet.activate();
    await context.sync();
}
export async function insertUpdateLog(context, data, timestamp, sourceSheet, line_items_column, comparison_column, target_column, overwrite_target, first_row_number, last_row_number) {
    // set calc mode to manual and save existing setting
    context.application.load("calculationMode");
    await context.sync();
    const userCalcMode = context.application.calculationMode;
    context.application.calculationMode = Excel.CalculationMode.manual;
    await context.sync();
    // write line items, comparison, and estimate columns
    const sheetName = `Update Log - ${timestamp}`;
    const sheet = context.workbook.worksheets.add(sheetName.substring(0, 30)); // Max sheet name 31 chars
    sheet.freezePanes.freezeRows(1);
    const sourceLineItems = sourceSheet.getRange(`${line_items_column}:${line_items_column}`);
    sheet.getRange("A:A").copyFrom(sourceLineItems, Excel.RangeCopyType.formats);
    sheet.getRange("A:A").copyFrom(sourceLineItems, Excel.RangeCopyType.values);
    const sourceComparisonColumn = sourceSheet.getRange(`${comparison_column}:${comparison_column}`);
    sheet
        .getRange("B:B")
        .copyFrom(sourceComparisonColumn, Excel.RangeCopyType.formats);
    sheet
        .getRange("B:B")
        .copyFrom(sourceComparisonColumn, Excel.RangeCopyType.values);
    const sourceTargetColumn = sourceSheet.getRange(`${target_column}:${target_column}`);
    sheet
        .getRange("C:C")
        .copyFrom(sourceTargetColumn, Excel.RangeCopyType.formats);
    sheet
        .getRange("C:C")
        .copyFrom(sourceTargetColumn, Excel.RangeCopyType.values);
    await context.sync();
    // update original model
    // copy comparison column formats & formulas to actual column
    const restrictedTargetColumn = sourceSheet
        .getRange(`${target_column}${first_row_number}:${target_column}${last_row_number}`)
        .getUsedRange();
    const restrictedComparisonColumn = sourceSheet
        .getRange(`${comparison_column}${first_row_number}:${comparison_column}${last_row_number}`)
        .getUsedRange();
    restrictedTargetColumn.copyFrom(restrictedComparisonColumn, Excel.RangeCopyType.formats);
    const formulaRanges = restrictedComparisonColumn.getSpecialCellsOrNullObject(Excel.SpecialCellType.formulas);
    const offsetRange = sourceSheet.getRange(`${comparison_column}:${target_column}`);
    offsetRange.load("columnCount");
    await context.sync();
    const actualOffset = offsetRange.columnCount - 1;
    if (!formulaRanges.isNullObject) {
        formulaRanges.load(["areaCount", "areas"]);
        await context.sync();
        const formulaRangeAreas = formulaRanges.areas;
        formulaRangeAreas.load(["items", "rowIndex", "rowCount"]);
        await context.sync();
        for (const formulaRange of formulaRangeAreas.items) {
            const actualFormulaRange = formulaRange.getOffsetRange(0, actualOffset);
            actualFormulaRange.copyFrom(formulaRange);
            actualFormulaRange.format.fill.color = "#DAE9F8";
            // write "formula" to source doc column in update log
            const firstRow = formulaRange.rowIndex + 1;
            const lastRow = formulaRange.rowIndex + formulaRange.rowCount;
            const rowCount = lastRow - firstRow + 1;
            sheet.getRange(`F${firstRow}:F${lastRow}`).values = Array.from({ length: rowCount }, () => ["Formula"]);
        }
    }
    // delete estimated numbers
    const restrictedRange = sourceSheet
        .getRange(`${target_column}${first_row_number}:${target_column}${last_row_number}`)
        .getUsedRangeOrNullObject();
    await context.sync();
    const targetNumberRanges = restrictedRange.getSpecialCellsOrNullObject(Excel.SpecialCellType.constants, Excel.SpecialCellValueType.numbers);
    targetNumberRanges.load("areas/items,areas/items/text,areas/items/rowIndex,");
    await context.sync();
    if (!targetNumberRanges.isNullObject) {
        // conditionally clear values if they are not in the "skip_list"
        const skip_list = await getSkipList(context, sourceSheet, line_items_column, target_column, overwrite_target);
        for (const range of targetNumberRanges.areas.items) {
            range.text.forEach((row, index) => {
                const rowNumber = index + range.rowIndex + 1;
                if (!skip_list.includes(rowNumber)) {
                    range.clear(Excel.ClearApplyTo.contents);
                }
            });
        }
    }
    // overwrite actuals in actual column
    for (const item of data.actual_period[0]) {
        if (item.row && item.text) {
            const cell = sourceSheet.getRange(`${target_column}${item.row}`);
            cell.values = [[item.text]];
            cell.format.fill.color = "SpringGreen";
            cell.format.font.color = "Black";
        }
    }
    // recalculate after adding actuals
    context.workbook.application.calculate("Full");
    // highlight warnings and errors
    for (const item of data.actual_period[2]) {
        const cell = sourceSheet.getRange(`${target_column}${item.row}`);
        if (item.row && item.text === "Error") {
            cell.format.fill.color = "Tomato";
            cell.values = [["No Value"]];
            cell.format.font.color = "White";
        }
        else if (item.row && item.text === "Warning") {
            cell.format.fill.color = "#FFFD8D";
            cell.format.font.color = "Black";
        }
    }
    // continue writing update log
    sheet
        .getRange("D:D")
        .copyFrom(sourceTargetColumn, Excel.RangeCopyType.formats);
    sheet
        .getRange("D:D")
        .copyFrom(sourceTargetColumn, Excel.RangeCopyType.values);
    sheet
        .getRange("E:E")
        .copyFrom(sourceComparisonColumn, Excel.RangeCopyType.formats);
    // location
    for (const item of data.actual_period[1]) {
        if (item.row && item.text) {
            const location_cell = sheet.getRange(`G${item.row}`);
            location_cell.values = [[item.text]];
        }
    }
    // source doc
    for (const item of data.actual_period[4]) {
        if (item.row && item.text && item.href) {
            const source_doc_cell = sheet.getRange(`F${item.row}`);
            source_doc_cell.values = [
                [`=hyperlink("${[[item.href]]}","${[[item.text]]}")`],
            ];
            source_doc_cell.format.font.underline = `Single`;
            source_doc_cell.format.font.color = `#0000FF`;
        }
    }
    // Anomaly
    for (const item of data.actual_period[3]) {
        if (item.row && item.text) {
            const cell = sheet.getRange(`I${item.row}`);
            cell.values = [[item.text]];
        }
    }
    // Anomaly level
    for (const item of data.actual_period[2]) {
        if (item.row && item.text && Number(item.row) > 1) {
            const level_cell = sheet.getRange(`H${item.row}`);
            level_cell.values = [[item.text]];
            // highlight entire row on errors
            const row = level_cell.getEntireRow();
            if (item.text === "Error") {
                row.format.fill.color = "Tomato";
                row.format.font.color = "White";
            }
        }
    }
    const detail_range = sheet.getRange(`F:I`);
    detail_range.format.columnWidth = 100;
    // update log headers
    sheet.getRange("A1").values = [[""]];
    sheet.getRange("B1").values = [["Comp"]];
    sheet.getRange("C1").values = [["Estimate"]];
    sheet.getRange("D1").values = [["Actual"]];
    sheet.getRange("E1").values = [["Surprise"]];
    sheet.getRange("F1").values = [["Source Doc"]];
    sheet.getRange("G1").values = [["Location"]];
    sheet.getRange("H1").values = [["Anomaly Level"]];
    sheet.getRange("I1").values = [["Anomaly"]];
    sheet
        .getRange("A1:I1")
        .copyFrom(sourceSheet.getRange("A1"), Excel.RangeCopyType.formats);
    // write surprise
    await context.sync();
    const actualColumn = sheet.getRange("D:D").getUsedRange();
    actualColumn.load({ select: "text,values" });
    await context.sync();
    actualColumn.values.forEach((row, index) => {
        const value = row[0];
        if (typeof value === "number") {
            const text = actualColumn.text[index][0];
            const surpriseCell = sheet.getRange(`E${index + 1}`);
            if (!text.includes("%")) {
                surpriseCell.formulas = [
                    [`=(D${index + 1}-C${index + 1})/C${index + 1}`],
                ];
                surpriseCell.numberFormat = [["0.0%"]];
            }
            else if (text.includes("%")) {
                surpriseCell.formulas = [
                    [`=TEXT(ROUND((D${index + 1}-C${index + 1})*10000,0),0)&" bps"`],
                ];
                surpriseCell.format.horizontalAlignment = "Right";
            }
        }
    });
    // reset calc mode to original
    context.workbook.application.calculate("Full");
    context.application.calculationMode = userCalcMode;
    await context.sync();
}
// Export only for testing
export const __test__ = {
    setManualCalculation,
    restoreCalculationMode,
    parseValueForExcel,
    buildValuesAndFormulas,
    formatSpecialCells,
    computeFormatGroups,
    columnIndexToColumnName,
    rowToRangeReference,
    blockToRangeReference,
    collapseToContiguousBlocks,
    applyFormattingBatched,
    findColumnForYearAndQuarter,
    findColumnForLineItems,
    setColumnBasedOnYearAndQuarter,
    setLineItemColumn,
    setSheetsSelectOptions,
    setActiveSheet,
    getContext,
    getSkipList,
    getColumnValues,
    buildModelUpdaterPayload,
    insertSummarySheet,
    insertUpdateLog,
    keyMetricsPhrases,
};
